import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { gameAPI, registrationAPI, drinksAPI } from '../utils/api';
import { getTicketTypeLabel, getDrinkLabel, generatePDF, generateExcel } from '../utils/gameExports';
import { toast } from 'react-toastify';
import './AdminGameDetail.css';

const AdminGameDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [drinkTypes, setDrinkTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(new Set());

  const loadGame = useCallback(async () => {
    try {
      const response = await gameAPI.getGame(id);
      if (response.success) {
        const g = response.game;
        setGame({
          id: g.id,
          opponent: g.opponent,
          date: g.game_date,
          time: g.game_time,
          location: g.location,
          category: g.category,
          maxRegistrations: g.max_registrations || 50,
          registrations: (g.registrations || []).map(reg => ({
            id: reg.id,
            userId: reg.user_id,
            firstName: reg.first_name || '',
            lastName: reg.last_name || '',
            name: reg.first_name && reg.last_name
              ? `${reg.first_name} ${reg.last_name}`
              : reg.username || 'Unbekannt',
            email: reg.email || '',
            ticketType: reg.ticket_type,
            drink: reg.drink || '',
            message: reg.message || '',
            address: reg.address || '',
            idCardNumber: reg.id_card_number || '',
            dateOfBirth: reg.date_of_birth || '',
            placeOfBirth: reg.place_of_birth || '',
            registeredAt: reg.created_at ? reg.created_at.split(' ')[0] : '',
            status: reg.status || 'pending',
            cancelledAt: reg.cancelled_at || null,
            cancelledFrom: reg.cancelled_from || null
          }))
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden des Spiels:', error);
      toast.error('Das Spiel konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadGame();
    (async () => {
      try {
        const response = await drinksAPI.getAllDrinks();
        if (response.success && response.drinks) {
          setDrinkTypes(response.drinks.map(d => ({ value: d.value, label: d.name })));
        }
      } catch (e) {
        // Ohne Liste zeigt getDrinkLabel den Rohwert - kein Grund abzubrechen
      }
    })();
  }, [loadGame]);

  const changeStatus = async (registrationId, status) => {
    if (status === 'cancelled' && !window.confirm('Diese Anmeldung wirklich ablehnen?')) return;
    setProcessing(prev => new Set([...prev, registrationId]));
    try {
      const response = await registrationAPI.updateRegistrationStatus(registrationId, status);
      if (response.success) {
        toast.success(status === 'confirmed' ? 'Anmeldung bestätigt' : 'Anmeldung abgelehnt');
        await loadGame();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Der Status konnte nicht geändert werden');
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(registrationId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="container"><div className="game-detail-admin"><p>Wird geladen …</p></div></div>
    );
  }

  if (!game) {
    return (
      <div className="container">
        <div className="game-detail-admin">
          <p>Dieses Spiel gibt es nicht (mehr).</p>
          <Link to="/admin" className="btn btn-secondary">Zurück zum Admin-Panel</Link>
        </div>
      </div>
    );
  }

  const registrations = game.registrations || [];
  const confirmed = registrations.filter(r => r.status === 'confirmed').length;
  const pending = registrations.filter(r => r.status === 'pending').length;
  const cancelled = registrations.filter(r => r.status === 'cancelled').length;
  const active = registrations.length - cancelled;
  const isChampionsLeague = game.category === 'champions-league';

  const statusLabel = (reg) => {
    if (reg.status === 'confirmed') return 'Bestätigt';
    if (reg.status !== 'cancelled') return 'Ausstehend';
    // cancelled_from zeigt an, ob das Mitglied selbst storniert hat: beim
    // Ablehnen durch den Vorstand wird die Spalte nicht gesetzt.
    return reg.cancelledFrom ? 'Storniert' : 'Abgelehnt';
  };

  const cancelNote = (reg) => {
    if (reg.status !== 'cancelled') return null;
    const datum = reg.cancelledAt ? new Date(reg.cancelledAt).toLocaleDateString('de-DE') : null;
    const vorher = reg.cancelledFrom === 'confirmed' ? 'war bereits bestätigt'
      : reg.cancelledFrom === 'pending' ? 'war noch ausstehend' : null;
    if (!datum && !vorher) return null;
    return [datum ? `am ${datum}` : null, vorher].filter(Boolean).join(', ');
  };

  return (
    <div className="container">
      <div className="game-detail-admin">
        <button onClick={() => navigate('/admin')} className="back-link">
          ← Zurück zur Spieleliste
        </button>

        <div className="detail-head">
          <h1>{game.opponent}</h1>
          <div className="detail-facts">
            <span><strong>Datum:</strong> {new Date(game.date).toLocaleDateString('de-DE')} um {(game.time || '').slice(0, 5)} Uhr</span>
            <span><strong>Ort:</strong> {game.location}</span>
            <span>
              <strong>Anmeldungen:</strong> {active}
              {' '}({confirmed} bestätigt, {pending} ausstehend
              {cancelled > 0 ? `, ${cancelled} storniert` : ''})
            </span>
          </div>

          <div className="detail-actions">
            <button
              onClick={() => generatePDF(game, drinkTypes)}
              className="btn btn-primary"
              disabled={registrations.length === 0}
            >
              📄 PDF Export
            </button>
            <button
              onClick={() => generateExcel(game, drinkTypes)}
              className="btn btn-success"
              disabled={registrations.length === 0}
            >
              📊 Excel Export
            </button>
          </div>
        </div>

        {registrations.length === 0 ? (
          <div className="empty-state">
            <p>Für dieses Spiel liegen noch keine Anmeldungen vor.</p>
          </div>
        ) : (
          <>
            {/* Tabelle ab Tablet-Breite */}
            <div className="detail-table-wrapper">
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th>Ticket</th>
                    <th>Getränk</th>
                    {isChampionsLeague && <th>Geburtsdatum</th>}
                    <th>Status</th>
                    <th>Angemeldet</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg, index) => (
                    <tr key={reg.id} className={`status-row-${reg.status}`}>
                      <td>{index + 1}</td>
                      <td className="cell-name">
                        <strong>{reg.name}</strong>
                        {reg.message && <div className="cell-note">{reg.message}</div>}
                      </td>
                      <td>{reg.email}</td>
                      <td>{getTicketTypeLabel(reg.ticketType)}</td>
                      <td>{getDrinkLabel(reg.drink, drinkTypes)}</td>
                      {isChampionsLeague && <td>{reg.dateOfBirth || '—'}</td>}
                      <td>
                        <span className={`reg-badge reg-${reg.status}`}>{statusLabel(reg)}</span>
                        {cancelNote(reg) && <div className="cell-note">{cancelNote(reg)}</div>}
                      </td>
                      <td>{reg.registeredAt ? new Date(reg.registeredAt).toLocaleDateString('de-DE') : '—'}</td>
                      <td>
                        <div className="row-actions">
                          <button
                            className="btn btn-sm btn-success"
                            title="Bestätigen"
                            disabled={processing.has(reg.id) || reg.status === 'confirmed'}
                            onClick={() => changeStatus(reg.id, 'confirmed')}
                          >
                            ✓
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            title="Ablehnen"
                            disabled={processing.has(reg.id) || reg.status === 'cancelled'}
                            onClick={() => changeStatus(reg.id, 'cancelled')}
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Karten auf schmalen Bildschirmen */}
            <div className="detail-cards">
              {registrations.map((reg, index) => (
                <div key={reg.id} className={`detail-card status-row-${reg.status}`}>
                  <div className="card-top">
                    <strong>{index + 1}. {reg.name}</strong>
                    <span className={`reg-badge reg-${reg.status}`}>{statusLabel(reg)}</span>
                  </div>
                  <div className="card-facts">
                    <div><strong>E-Mail:</strong> {reg.email}</div>
                    <div><strong>Ticket:</strong> {getTicketTypeLabel(reg.ticketType)}</div>
                    <div><strong>Getränk:</strong> {getDrinkLabel(reg.drink, drinkTypes)}</div>
                    {isChampionsLeague && reg.dateOfBirth && (
                      <div><strong>Geburtsdatum:</strong> {reg.dateOfBirth}</div>
                    )}
                    <div><strong>Angemeldet:</strong> {reg.registeredAt ? new Date(reg.registeredAt).toLocaleDateString('de-DE') : '—'}</div>
                    {cancelNote(reg) && <div><strong>Storniert:</strong> {cancelNote(reg)}</div>}
                    {reg.message && <div><strong>Nachricht:</strong> {reg.message}</div>}
                  </div>
                  <div className="card-actions">
                    <button
                      className="btn btn-sm btn-success"
                      disabled={processing.has(reg.id) || reg.status === 'confirmed'}
                      onClick={() => changeStatus(reg.id, 'confirmed')}
                    >
                      ✓ Bestätigen
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      disabled={processing.has(reg.id) || reg.status === 'cancelled'}
                      onClick={() => changeStatus(reg.id, 'cancelled')}
                    >
                      ✕ Ablehnen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminGameDetail;
